// eslint-disable-next-line import/no-extraneous-dependencies
import { sanitize } from 'lettersanitizer';

import type { LetterContent } from '@/types/recipientEmail';

import { sanitizeCandidateStylesheet } from './candidateStylesheet';
import { readableCandidateText } from './candidateFallback';
import { candidateRootAttributes } from './candidateRootAttributes';
import { visibleCandidateFallback } from './candidateVisibility';
import { stripCandidateResourceFunctions } from './candidateCssResources';

export interface CandidateDocument {
    srcDoc: string;
    direction: 'rtl' | 'ltr' | 'auto';
    blockedResources: number;
    fallbackText: string;
    layoutWarnings: string[];
    documentId: string;
    usesStaticFallback: boolean;
}

export const normalizeContentId = (value: string): string => {
    let decoded = value.trim().replace(/^cid:/i, '');
    try {
        decoded = decodeURIComponent(decoded);
    } catch {
        // Malformed percent escapes must not turn an otherwise readable email into a blank page.
    }
    return decoded.replace(/^<|>$/g, '');
};

export const safeEmailLink = (url: string): string => {
    const trimmed = url.trim();
    if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
    if (trimmed.startsWith('#')) return trimmed;
    return '';
};

const isSafeRaster = (value: string): boolean => /^data:image\/(png|gif|jpe?g|webp|avif|bmp);base64,/i.test(value);
const MAX_AUTHORED_SPACER_PX = 100000;
const MAX_HTML_ELEMENTS = 20000;
let nextDocumentId = 0;

const normalizeAuthorLayout = (style: CSSStyleDeclaration): void => {
    Array.from(style).forEach((property) => {
        const value = style.getPropertyValue(property);
        const safeEscapes = value.replace(/url\([^)]*\)/gi,
            (url) => url.replace(/%(?![a-f\d]{2})/gi, '%25'));
        if (safeEscapes !== value) style.setProperty(property, safeEscapes, style.getPropertyPriority(property));
        if (/(?:\d|\.)(?:dvh|svh|lvh|vh|vmin|vmax)\b/i.test(value)
            || (/(?:^|-)(?:height|block-size)$/.test(property) && value.includes('%'))
            || (/(?:^|-)(?:height|block-size)$/.test(property)
                && /^\d+(?:\.\d+)?px$/i.test(value) && parseFloat(value) > MAX_AUTHORED_SPACER_PX)
            || property === 'zoom') {
            style.removeProperty(property);
        }
    });
};

const SIZING_CSS = `
html { overflow-x: auto !important; overflow-y: hidden !important; }
html, body {
    height: auto !important; min-height: 0 !important; max-height: none !important;
    min-width: 0 !important; max-width: 100% !important;
    zoom: 1 !important; transform: none !important;
}
body { display: flow-root !important; overflow: visible !important; overflow-wrap: anywhere; }
img { max-width: 100% !important; height: auto !important; object-fit: contain; }
table { max-width: 100%; }
pre { white-space: pre-wrap; overflow-wrap: anywhere; }
*, *::before, *::after { animation: none !important; transition: none !important; }
`;

const CSP = [
    "default-src 'none'", "script-src 'none'", "style-src 'unsafe-inline'", 'img-src data:',
    "font-src 'none'", "connect-src 'none'", "media-src 'none'", "object-src 'none'",
    "frame-src 'none'", "base-uri 'none'", "form-action 'none'",
].join('; ');

export const buildCandidateDocument = ({
    html, plainText, attachments = [],
}: Omit<LetterContent, 'subject'>, fallbackViewportWidth?: number): CandidateDocument => {
    const parser = new DOMParser();
    const layoutWarnings: string[] = [];
    let blockedResources = 0;
    const attachmentMap = new Map(attachments.map(({ cid, link }) => [normalizeContentId(cid), link]));
    const rewriteResource = (url: string): string => {
        const linked = /^cid:/i.test(url) ? attachmentMap.get(normalizeContentId(url)) : url;
        if (linked && isSafeRaster(linked)) return linked;
        if (url) blockedResources += 1;
        return '';
    };
    const rewriteStyleResources = (style: CSSStyleDeclaration): void => {
        Array.from(style).forEach((property) => {
            const filtered = stripCandidateResourceFunctions(style.getPropertyValue(property), true);
            const value = filtered.css;
            blockedResources += filtered.blockedResources;
            if (filtered.blockedResources) {
                style.setProperty(property, value, style.getPropertyPriority(property));
            }
            if (/image-set\s*\(/i.test(value)) {
                blockedResources += 1;
                style.removeProperty(property);
                return;
            }
            let blocked = false;
            const safeValue = value.replace(/url\(\s*(["']?)(.*?)\1\s*\)/gi, (_match, _quote, url) => {
                const safe = rewriteResource(url);
                if (!safe) blocked = true;
                return `url("${safe}")`;
            });
            if (blocked) style.removeProperty(property);
            else if (safeValue !== value) style.setProperty(property, safeValue, style.getPropertyPriority(property));
        });
    };
    const template = document.createElement('template');
    template.innerHTML = html || '';
    template.content.querySelectorAll(
        'script, iframe, object, embed, link, meta, svg, audio, video, source, track, template',
    ).forEach((element) => element.remove());
    template.content.querySelectorAll('*').forEach((element) => {
        ['src', 'srcset', 'poster', 'background', 'data', 'ping'].forEach((attribute) => {
            if (!element.hasAttribute(attribute)) return;
            const resource = element.getAttribute(attribute) || '';
            element.removeAttribute(attribute);
            if (element.tagName === 'IMG' && attribute === 'src') {
                const safe = rewriteResource(resource);
                if (safe) element.setAttribute('src', safe);
            }
        });
    });
    const authorStyles = Array.from(template.content.querySelectorAll('style'), (style) => {
        const css = style.textContent || '';
        style.remove();
        return css;
    });
    const original = document.implementation.createHTMLDocument();
    original.body.append(template.content);
    const rootAttributes = candidateRootAttributes(html || '');
    (['html', 'body'] as const).forEach((name) => {
        const rootSource = rootAttributes[name];
        if (rootSource === undefined) return;
        const attributes = document.createElement('template');
        attributes.innerHTML = `<div ${rootSource}></div>`;
        const target = name === 'html' ? original.documentElement : original.body;
        Array.from(attributes.content.firstElementChild?.attributes || []).forEach(({ name: attribute, value }) => {
            if (['dir', 'lang', 'style', 'class', 'id'].includes(attribute)) target.setAttribute(attribute, value);
        });
    });
    let fallbackText = html ? readableCandidateText(original.body) : plainText || '';
    let recoveredVisibility = false;
    const recoverFallbackVisibility = (): void => {
        if (recoveredVisibility || !html || authorStyles.length === 0) return;
        recoveredVisibility = true;
        const visible = visibleCandidateFallback(original.body, authorStyles, fallbackViewportWidth);
        fallbackText = visible.text;
        if (visible.warning) layoutWarnings.push(visible.warning);
    };
    const sanitizerOptions = {
        noWrapper: true,
        rewriteExternalResources: rewriteResource,
        rewriteExternalLinks: safeEmailLink,
    };

    original.querySelectorAll<HTMLElement>('[dir]').forEach((element) => {
        const direction = element.getAttribute('dir');
        if (direction === 'rtl' || direction === 'ltr') element.style.setProperty('direction', direction);
    });
    original.querySelectorAll<HTMLElement>('[hidden]').forEach((element) => {
        element.style.setProperty('display', 'none');
    });
    original.querySelectorAll<HTMLAnchorElement>('a[name]').forEach((anchor) => {
        const name = anchor.getAttribute('name');
        if (!name || original.getElementById(name)) return;
        if (!anchor.id) anchor.setAttribute('id', name);
        else {
            const alias = original.createElement('span');
            alias.id = name;
            anchor.prepend(alias);
        }
    });
    original.querySelectorAll('bdi, bdo').forEach((element) => {
        const replacement = original.createElement('span');
        replacement.style.unicodeBidi = element.tagName === 'BDI' ? 'isolate' : 'bidi-override';
        const direction = element.getAttribute('dir');
        if (direction === 'ltr' || direction === 'rtl') replacement.style.direction = direction;
        replacement.append(...Array.from(element.childNodes));
        element.replaceWith(replacement);
    });
    original.querySelectorAll<HTMLElement>('[style], style').forEach((element) => {
        const normalize = (value: string): string => value.replace(/url\([^)]*\)/gi,
            (url) => url.replace(/%(?![a-f\d]{2})/gi, '%25'));
        if (element.tagName === 'STYLE') element.replaceChildren(normalize(element.textContent || ''));
        else {
            const filtered = stripCandidateResourceFunctions(element.getAttribute('style') || '', true);
            blockedResources += filtered.blockedResources;
            element.setAttribute('style', normalize(filtered.css));
            normalizeAuthorLayout(element.style);
            rewriteStyleResources(element.style);
        }
    });

    const result = document.implementation.createHTMLDocument('Email message');
    nextDocumentId += 1;
    const documentId = String(nextDocumentId);
    const meta = result.createElement('meta');
    meta.setAttribute('http-equiv', 'Content-Security-Policy');
    meta.setAttribute('content', CSP);
    result.head.prepend(meta);
    const viewport = result.createElement('meta');
    viewport.name = 'viewport';
    viewport.content = 'width=device-width, initial-scale=1';
    result.head.appendChild(viewport);
    const charset = result.createElement('meta');
    charset.setAttribute('charset', 'utf-8');
    result.head.prepend(charset);
    let simplified = original.getElementsByTagName('*').length > MAX_HTML_ELEMENTS;

    try {
        if (simplified) {
            recoverFallbackVisibility();
            const pre = result.createElement('pre');
            pre.textContent = fallbackText || 'This email has no readable message text.';
            result.body.appendChild(pre);
            layoutWarnings.push('This unusually complex email is shown as text to keep the viewer responsive.');
        } else if (html) {
            result.body.innerHTML = sanitize(original.documentElement.outerHTML, plainText, sanitizerOptions);
        } else {
            const pre = result.createElement('pre');
            pre.textContent = plainText || 'This email has no message body.';
            pre.style.margin = '0';
            pre.style.font = 'inherit';
            result.body.appendChild(pre);
        }
        if (!simplified) {
            [original.documentElement, original.body].forEach((source, index) => {
                const target = index === 0 ? result.documentElement : result.body;
                const probe = original.createElement('div');
                ['style', 'dir', 'class', 'id'].forEach((attribute) => {
                    if (source.hasAttribute(attribute)) probe.setAttribute(attribute, source.getAttribute(attribute));
                });
                const cleanRoot = parser.parseFromString(sanitize(probe.outerHTML, '', sanitizerOptions), 'text/html');
                Array.from(cleanRoot.body.firstElementChild?.attributes ?? []).forEach(({ name, value }) => {
                    target.setAttribute(name, value);
                });
                const language = source.getAttribute('lang');
                if (language && /^[a-z\d-]{1,35}$/i.test(language)) target.setAttribute('lang', language);
            });
        }
        if (!simplified) {
            authorStyles.forEach((css) => {
                const style = result.createElement('style');
                style.textContent = sanitizeCandidateStylesheet(css, (declarations) => {
                    const probe = original.createElement('div');
                    probe.setAttribute('style', declarations);
                    normalizeAuthorLayout(probe.style);
                    rewriteStyleResources(probe.style);
                    const clean = parser.parseFromString(sanitize(probe.outerHTML, '', sanitizerOptions), 'text/html');
                    return clean.body.firstElementChild?.getAttribute('style') || '';
                });
                result.body.appendChild(style);
            });
        }
    } catch {
        result.body.replaceChildren();
        recoverFallbackVisibility();
        const pre = result.createElement('pre');
        pre.textContent = fallbackText || 'This email has no readable message text.';
        result.body.appendChild(pre);
        result.documentElement.removeAttribute('style');
        result.body.removeAttribute('style');
        simplified = true;
        layoutWarnings.push('Some unsupported formatting could not be decoded. The message is shown as text.');
    }

    if (!readableCandidateText(result.body) && !result.body.querySelector('img, hr')) {
        const empty = result.createElement('p');
        empty.textContent = 'This email has no message body.';
        result.body.prepend(empty);
    }

    result.body.querySelectorAll('img').forEach((image) => {
        if (!image.getAttribute('src')) {
            image.removeAttribute('src');
            if (!image.alt) image.setAttribute('alt', 'Image unavailable');
        }
    });
    result.body.querySelectorAll('a').forEach((link) => {
        if (!link.getAttribute('href')) link.removeAttribute('href');
        link.setAttribute('rel', 'noopener noreferrer');
        link.setAttribute('target', link.getAttribute('href')?.startsWith('#') ? '_self' : '_blank');
    });
    const declaredDirection = result.body.style.direction || result.body.getAttribute('dir')
        || result.documentElement.style.direction || result.documentElement.getAttribute('dir');
    const direction = declaredDirection === 'rtl' || declaredDirection === 'ltr' ? declaredDirection : 'auto';
    result.documentElement.setAttribute('dir', direction);
    result.documentElement.setAttribute('data-candidate-document', documentId);
    if (!result.body.style.margin) result.body.style.margin = '0';
    [result.documentElement, result.body].forEach((root) => {
        Object.entries({
            height: 'auto',
            'min-height': '0',
            'max-height': 'none',
            'min-width': '0',
            'max-width': '100%',
            zoom: '1',
            transform: 'none',
            visibility: 'visible',
            opacity: '1',
        }).forEach(([property, value]) => root.style.setProperty(property, value, 'important'));
    });
    result.documentElement.style.setProperty('display', 'block', 'important');
    result.documentElement.style.setProperty('overflow-x', 'auto', 'important');
    result.documentElement.style.setProperty('overflow-y', 'hidden', 'important');
    result.body.style.setProperty('display', 'flow-root', 'important');
    result.body.style.setProperty('overflow', 'visible', 'important');
    const sizing = result.createElement('style');
    sizing.setAttribute('data-candidate-sizing', '');
    sizing.textContent = SIZING_CSS;
    result.body.appendChild(sizing);
    return {
        srcDoc: `<!doctype html>${result.documentElement.outerHTML}`,
        direction,
        blockedResources,
        fallbackText,
        layoutWarnings,
        documentId,
        usesStaticFallback: simplified,
    };
};
